/**
 * usePrefetchRoute — 200ms-delay prefetch on hover/focus for route Link elements.
 *
 * Accepts a prefetch callback (typically a dynamic import factory). When the
 * user hovers (onPointerEnter) or focuses a link for more than 200ms, the
 * callback is invoked to trigger the lazy-chunk load.
 *
 * - onPointerLeave / onBlur before 200ms cancels the prefetch.
 * - Duplicate prefetches for the same callback are suppressed (once fired,
 *   the callback is not invoked again on subsequent hover cycles).
 *
 * @see specs/prd.md FR-021
 * @see specs/plan.md Phase F-8
 */
import { useCallback, useRef, useEffect } from "react";

// ---------------------------------------------------------------------------
// Deduplication set — tracks which callbacks have already been fired
// ---------------------------------------------------------------------------

const prefetchedSet = new WeakSet<() => Promise<unknown>>();

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface PrefetchHandlers {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Hook that returns event handlers for prefetching a route's lazy chunk.
 *
 * @param prefetchFn — a factory function (e.g. `() => import("./screens/Foo")`)
 * @returns spread-able event handler props
 */
export function usePrefetchRoute(
  prefetchFn: () => Promise<unknown>,
): PrefetchHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(prefetchFn);
  fnRef.current = prefetchFn;

  const startPrefetch = useCallback(() => {
    // Already prefetched — skip
    if (prefetchedSet.has(fnRef.current)) return;

    // Clear any existing timer
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const fn = fnRef.current;
      if (prefetchedSet.has(fn)) return;

      prefetchedSet.add(fn);
      void fn();
    }, 200);
  }, []);

  const cancelPrefetch = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    onPointerEnter: startPrefetch,
    onPointerLeave: cancelPrefetch,
    onFocus: startPrefetch,
    onBlur: cancelPrefetch,
  };
}
